import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Briefcase, ArrowLeft, ExternalLink, Monitor, Headphones, Star, Armchair, Laptop, Wifi, Sun, Mouse } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORY_ICONS = { Camera: Monitor, Audio: Headphones, Chair: Armchair, Monitor: Monitor, Desk: Laptop, Peripherals: Mouse, Lighting: Sun, Organization: Laptop, Internet: Wifi };

export default function EquipmentResources() {
  const [equipment, setEquipment] = useState([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    axios.get(`${API}/equipment`).then(r => setEquipment(r.data.equipment)).catch(console.error);
  }, []);

  const categories = ['all', ...new Set(equipment.map(e => e.category))];
  const filtered = filter === 'all' ? equipment : equipment.filter(e => e.category === filter);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="glass sticky top-0 z-50 border-b border-[#E5E0D8]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center h-16">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#047857] flex items-center justify-center"><Briefcase size={14} className="text-white" strokeWidth={1.5} /></div>
            <span className="text-lg font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>WorkFromJoy</span>
          </Link>
        </div>
      </nav>
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-[#047857] mb-4"><ArrowLeft size={14} strokeWidth={1.5} /> Dashboard</Link>
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl tracking-tight font-medium" style={{fontFamily:'Outfit'}}>WFH Equipment Guide</h1>
          <p className="text-[#1C2A24]/60 mt-2">Curated equipment recommendations to set up your perfect home office</p>
        </div>
        {/* Category Filters */}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="equipment-filters">
          {categories.map(cat => (
            <button key={cat} onClick={() => setFilter(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${filter === cat ? 'bg-[#047857] text-white' : 'bg-[#F4F1E8] text-[#1C2A24]/70 hover:bg-[#E5E0D8]'}`}>
              {cat === 'all' ? 'All' : cat}
            </button>
          ))}
        </div>
        {/* Equipment Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="equipment-grid">
          {filtered.map(item => {
            const Icon = CATEGORY_ICONS[item.category] || Monitor;
            return (
              <div key={item.id} className="bg-white rounded-3xl border border-[#E5E0D8] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(4,120,87,0.08)] transition-all duration-300" data-testid={`equipment-${item.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-2xl bg-[#A7F3D0]/30 flex items-center justify-center">
                    <Icon size={22} className="text-[#047857]" strokeWidth={1.5} />
                  </div>
                  <div className="flex items-center gap-1">
                    <Star size={12} className="text-[#D97757] fill-[#D97757]" strokeWidth={1.5} />
                    <span className="text-xs text-[#1C2A24]/60">{item.rating}</span>
                  </div>
                </div>
                <h3 className="font-medium text-base mb-1" style={{fontFamily:'Outfit'}}>{item.name}</h3>
                <p className="text-sm text-[#1C2A24]/50 mb-3">{item.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-lg font-semibold text-[#047857]" style={{fontFamily:'Outfit'}}>{item.price}</span>
                  <a href={item.url} target="_blank" rel="noopener noreferrer">
                    <Button size="sm" className="rounded-full bg-[#D97757] hover:bg-[#C4623F] text-white text-xs h-8 px-4" data-testid={`buy-${item.id}`}>
                      View on Amazon <ExternalLink size={12} className="ml-1" strokeWidth={1.5} />
                    </Button>
                  </a>
                </div>
                <span className="tag-pill text-[10px] mt-3 inline-block">{item.category}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
